import {UIGameManager} from '../UIGameManager'
import {UIGameActionButtonView} from './UIGameActionButtonView'
import {PIXI} from '@colonist/pixi'
import {UIGameActionButtonViewWithBadge} from './UIGameActionButtonViewWithBadge'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {ActionsThatCanBeCancelled} from '@colonist/game-base-types'
import {logError} from '../../ui_utils/Utils'
import {ArrayUtils} from '../../../../../shared/utils/ArrayUtils'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {ActionState, MechanicKnightState, MechanicPirateState, MechanicRobberState, PlayerColor, TurnState} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {MechanicValidatorKnightActions} from '@colonist/game-mechanic-validators'

// May 16, 2021. Jeff: This is code that can be used when the masking is figured out
// const knightToShow = TileCornerBuildingType.KnightActiveLevel1
// const knightTexture = UIGameSpriteHandler.getKnightForPlayer(uiGameManager.gameController.myColor, knightToShow)
// const knightX = this.width * 0.12
// const kngihtY = 0
// const knightW = this.width * 0.6
// const knightH = knightTexture.width / knightTexture.height * knightW
// const knightIcon = new SpriteView(knightX, kngihtY, knightW, knightH, this.viewContainer, knightTexture)
// this.addAsset(knightIcon)
// this.centerIconInY(knightIcon)

// const arrowTexture = UIGameSpriteHandler.getTexture(Sprites.icon_arrow_white)
// const arrowX = this.width * 0.52
// const arrowY = 0
// const arrowH = this.height * 0.43
// const arrowW = arrowTexture.width / arrowTexture.height * arrowH
// const arrowIcon = new SpriteView(arrowX, arrowY, arrowW, arrowH, this.viewContainer, arrowTexture)
// this.addAsset(arrowIcon)
// this.centerIconInY(arrowIcon)

export class UIGameActionButtonViewKnightActions extends UIGameActionButtonViewWithBadge {
    private readonly parentButton: UIGameActionButtonView
    private readonly knightActionsValidator: MechanicValidatorKnightActions

    constructor(uiGameManager: UIGameManager, container: PIXI.Container, parentButton: UIGameActionButtonView, knightState: MechanicKnightState, robberState: MechanicRobberState | undefined, pirateState: MechanicPirateState | undefined) {
        const preferredPosition = 4
        const startingAmount = 0
        super(uiGameManager, preferredPosition, container, startingAmount)

        const texture = UIGameActionButtonViewKnightActions.getMoveKnightTextureForColor(uiGameManager.gameController.myColor, this.uiGameManager.gameController.isPlayerSpectator)
        const sizeRatio = 0.75
        this.addCenteredIcon(texture, sizeRatio)

        this.parentButton = parentButton
        const playerValidators = this.uiGameManager.gameController.getPlayerValidators()
        const mapValidator = this.uiGameManager.gameController.mapValidator
        this.knightActionsValidator = new MechanicValidatorKnightActions(playerValidators, mapValidator, knightState, robberState, pirateState)

        this.setTip({key: 'strings:game.tooltips.knightAction'}, [{key: 'strings:colonistCommon.utils.emptyString'}])

        this.setUpdateAction()
        this.stateUpdated()
    }

    buttonSpecificEvents(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.PlayerControllerStateUpdated:
            case UIGameEventEnums.KnightStateUpdated:
                this.stateUpdated()
                this.playerStateUpdated()
                break
            case UIGameEventEnums.GameStateUpdated: this.stateUpdated(); break
            case UIGameEventEnums.ShowTradeUI: this.cancelBuild(); break
            default: break
        }
    }

    private setUpdateAction() {
        this.updateAction(() => {
            this.clickedKnightAction()
        })
    }

    protected playerStateUpdated() {
        const locationsEligibleForActions = this.knightActionsValidator.getAllKnightLocationsEligibleForActions(this.uiGameManager.gameController.myColor)
        this.updateBadgeCount(locationsEligibleForActions.length)
    }

    private clickedKnightAction() {
        if(this.cancelBuild()) return

        const canPlayerTakeKnightAction = this.knightActionsValidator.canPlayerTakeKnightAction(this.uiGameManager.gameController.myColor)
        if(!this.canPerformBuyAction(canPlayerTakeKnightAction)) return

        this.uiGameManager.socketGameSend.takeKnightAction()
    }

    private allowableActionStates(): boolean {
        if(this.uiGameManager.gameController.currentState.actionState == ActionState.SelectKnightToTakeAction) return true
        if(this.uiGameManager.gameController.currentState.actionState == ActionState.SelectKnightAction) return true

        return false
    }

    private cancelBuild(): boolean {
        if(!this.uiGameManager.gameController.isMyTurn) return false
        if(!this.allowableActionStates()) return false

        this.uiGameManager.eventController.sendEvent([], UIGameEventEnums.SetCornerHighlight)
        this.uiGameManager.eventController.sendEvent([], UIGameEventEnums.SetTileHighlight)
        this.uiGameManager.socketGameSend.cancelAction()
        return true
    }

    private canPlayerTakeKnightAction(): boolean {
        const canPlayerTakeKnightAction = this.knightActionsValidator.canPlayerTakeKnightAction(this.uiGameManager.gameController.myColor)
        return canPlayerTakeKnightAction.canTakeAction
    }

    protected stateUpdated() {
        this.inactivateOpacity()
        this.removeHighlight()

        if(!this.uiGameManager.gameController.isMyTurn) return
        if(!this.canPlayerTakeKnightAction()) return

        this.updateOpacityForTurnState()
        this.updateHighlightForActionState()
    }

    private updateOpacityForTurnState() {
        if(!this.allowableTurnStates()) return
        if(!ArrayUtils.elementInArray(ActionsThatCanBeCancelled.actions, this.uiGameManager.gameController.currentState.actionState)) return
        this.activateOpacity()
        this.parentButton.activateOpacity()
    }

    private allowableTurnStates(): boolean {
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.Turn) return true
        return false
    }

    private updateHighlightForActionState() {
        if(!this.allowableActionStates()) return
        this.activateOpacity()
        this.highlight()
    }

    // May 16, 2021. Jeff: This code shouldn't be used anywhere else and is temporary until the PIXI masking is figured out
    static getMoveKnightTextureForColor(playerColor: PlayerColor, isSpectator: boolean): PIXI.Texture {
        if(isSpectator) return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_green)
        switch(playerColor) {
            case PlayerColor.Blue: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_blue)
            case PlayerColor.Red: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_red)
            case PlayerColor.Orange: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_orange)
            case PlayerColor.Green: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_green)
            case PlayerColor.Bronze: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_bronze)
            case PlayerColor.Silver: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_silver)
            case PlayerColor.Gold: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_gold)
            case PlayerColor.Black: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_black)
            case PlayerColor.Purple: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_purple)
            case PlayerColor.MysticBlue: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_mysticblue)
            case PlayerColor.White: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_white)
            case PlayerColor.Pink: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_pink)
            case PlayerColor.None: break
        }
        logError('getMoveKnightTextureForColor', [playerColor])
        return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.knight_move_green)
    }
}

